import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { userLogin } from "../store/slices/auth";

import {
    FiEye,
    FiEyeOff,
    FiLock,
    FiUser,
    FiArrowRight,
    FiShield,
} from "react-icons/fi";


function validate(values) {
    const errors = {};

    if (!values.username?.trim()) {
        errors.username = "Please enter your User ID.";
    }

    if (!values.password) {
        errors.password = "Please enter your password.";
    }

    return errors;
}


export default function Login() {

    const [values, setValues] = useState({
        username: "",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const visualRef = useRef(null);


    /* =========================
       BACKGROUND MOUSE EFFECT
    ========================= */

    useEffect(() => {

        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        const visual = visualRef.current;

        const blobs = visual
            ? visual.querySelectorAll(":scope > .blob")
            : [];

        if (
            !visual ||
            !blobs.length ||
            reduceMotion ||
            !window.matchMedia("(pointer: fine)").matches
        ) {
            return;
        }

        const onMouseMove = (e) => {

            const rect = visual.getBoundingClientRect();

            const cx =
                (e.clientX - rect.left) / rect.width - 0.5;

            const cy =
                (e.clientY - rect.top) / rect.height - 0.5;

            blobs.forEach((blob, i) => {

                const strength = 15 + i * 7;

                blob.style.translate =
                    `${cx * strength}px ${cy * strength} px`;

            });
        };

        visual.addEventListener("mousemove", onMouseMove, {
            passive: true,
        });

        return () =>
            visual.removeEventListener(
                "mousemove",
                onMouseMove
            );

    }, []);


    /* =========================
       INPUT CHANGE
    ========================= */

    function handleChange(e) {

        const { name, value } = e.target;

        const nextValues = {
            ...values,
            [name]: value,
        };

        setValues(nextValues);

        const nextErrors = validate(nextValues);

        setErrors((prev) => {

            const updatedErrors = {
                ...prev,
            };

            if (nextErrors[name]) {
                updatedErrors[name] = nextErrors[name];
            } else {
                delete updatedErrors[name];
            }

            return updatedErrors;
        });
    }


    /* =========================
       INPUT BLUR
    ========================= */

    function handleBlur(e) {

        const { name } = e.target;

        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));

        const nextErrors = validate(values);

        setErrors((prev) => {

            const updatedErrors = {
                ...prev,
            };

            if (nextErrors[name]) {
                updatedErrors[name] = nextErrors[name];
            } else {
                delete updatedErrors[name];
            }

            return updatedErrors;
        });
    }


    /* =========================
       LOGIN
    ========================= */

    async function handleSubmit(e) {

        e.preventDefault();

        const nextErrors = validate(values);

        setErrors(nextErrors);

        setTouched({
            username: true,
            password: true,
        });

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        try {

            setSubmitting(true);
            setNote(null);

            const payload = {
                username: values.username.trim(),
                password: values.password,
            };

            const response = await dispatch(
                userLogin(payload)
            ).unwrap();

            if (response?.status !== 1) {
                throw new Error(
                    response?.message || "Login failed"
                );
            }

            const userData = response?.data;

            if (!userData) {
                throw new Error(
                    response?.message ||
                    "User data not received"
                );
            }

            const {
                accessToken,
                refreshToken,
                password,
                ...user
            } = userData;

            if (!accessToken) {
                throw new Error(
                    "Access token not received from server"
                );
            }

            sessionStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            sessionStorage.setItem(
                "accessToken",
                accessToken
            );

            if (refreshToken) {

                sessionStorage.setItem(
                    "refreshToken",
                    refreshToken
                );
            }

            sessionStorage.setItem(
                "isLoggedIn",
                "true"
            );

            toast.success(
                response?.message ||
                "Login successful"
            );

            navigate("/dashboard", {
                replace: true,
            });

        } catch (error) {

            console.error(
                "LOGIN ERROR:",
                error
            );

            toast.error(
                typeof error === "string"
                    ? error
                    : error?.message ||
                    error?.msg ||
                    "Login failed. Please check your credentials."
            );

        } finally {

            setSubmitting(false);
        }
    }


    return (
        <main className="modern-login">

            <section className="login-form-section">

                <div className="login-form-container">

                    {/* =========================
                    LOGO
                ========================= */}

                    <div className="mobile-brand">

                        <div className="brand-icon">
                            <FiShield size={23} />
                        </div>

                        <div className="brand-text">
                            <strong>Admin Portal</strong>
                            <span>Management System</span>
                        </div>

                    </div>


                    {/* =========================
                    LOGIN HEADER
                ========================= */}

                    <div className="login-header">

                        <span className="login-eyebrow">
                            ADMINISTRATION
                        </span>

                        <h1>
                            Welcome back
                        </h1>

                        <p>
                            Sign in to continue to your dashboard.
                        </p>

                    </div>


                    {/* =========================
                    LOGIN FORM
                ========================= */}

                    <form
                        noValidate
                        onSubmit={handleSubmit}
                        className="modern-login-form"
                    >

                        {/* USER ID */}

                        <div
                            className={`modern-field ${errors.username &&
                                    touched.username
                                    ? "has-error"
                                    : ""
                                }`}
                        >

                            <label htmlFor="username">
                                User ID
                            </label>

                            <div className="input-wrapper">

                                <FiUser
                                    className="input-icon"
                                    size={19}
                                />

                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    placeholder="Enter your User ID"
                                    autoComplete="username"
                                    value={values.username}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    aria-invalid={
                                        !!errors.username
                                    }
                                />

                            </div>

                            {errors.username &&
                                touched.username && (
                                    <span className="modern-error">
                                        {errors.username}
                                    </span>
                                )}

                        </div>


                        {/* PASSWORD */}

                        <div
                            className={`modern-field ${errors.password &&
                                    touched.password
                                    ? "has-error"
                                    : ""
                                }`}
                        >

                            <label htmlFor="password">
                                Password
                            </label>

                            <div className="input-wrapper">

                                <FiLock
                                    className="input-icon"
                                    size={19}
                                />

                                <input
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    id="password"
                                    name="password"
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    aria-invalid={
                                        !!errors.password
                                    }
                                />

                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() =>
                                        setShowPassword(
                                            (prev) => !prev
                                        )
                                    }
                                    aria-label={
                                        showPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                >
                                    {showPassword ? (
                                        <FiEyeOff size={19} />
                                    ) : (
                                        <FiEye size={19} />
                                    )}
                                </button>

                            </div>

                            {errors.password &&
                                touched.password && (
                                    <span className="modern-error">
                                        {errors.password}
                                    </span>
                                )}

                        </div>


                        {/* LOGIN BUTTON */}

                        <button
                            type="submit"
                            className="modern-login-button"
                            disabled={submitting}
                        >

                            <span>
                                {submitting
                                    ? "Signing in..."
                                    : "Sign in"}
                            </span>

                            {!submitting && (
                                <FiArrowRight size={20} />
                            )}

                        </button>


                        {/* SECURITY NOTE */}

                        <div className="security-note">

                            <FiShield size={15} />

                            <span>
                                Your login information is
                                securely encrypted.
                            </span>

                        </div>

                    </form>


                    {/* =========================
                    FOOTER
                ========================= */}

                    <div className="login-footer">

                        <span>
                            © {new Date().getFullYear()} Admin Portal
                        </span>

                        <span className="footer-dot">
                            •
                        </span>

                        <span>
                            Secure Administration
                        </span>

                    </div>

                </div>

            </section>

        </main>
    );
}
